import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, CircularProgress, Box } from "@mui/material";
import { Cropper } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";

const ImageCropperModal = ({ cropSrc, isOpen, toggle, onImageCropped, onCancel, isMobile }) => {
  const [loader, setLoader] = useState(false);
  const cropperRef = useRef(null);
  const [cropper, setCropper] = useState(null);

  const handleCrop = async () => {
    setLoader(true);
    try {
      if (cropperRef.current) {
        const canvas = cropperRef.current.getCanvas();
        if (canvas) {
          // Create a new canvas with white background
          const finalCanvas = document.createElement("canvas");
          const ctx = finalCanvas.getContext("2d");
          finalCanvas.width = canvas.width;
          finalCanvas.height = canvas.height;

          // Fill white background
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

          // Draw the cropped image on top
          ctx.drawImage(canvas, 0, 0);

          const croppedImage = finalCanvas.toDataURL("image/jpeg", 0.9);
          onImageCropped(croppedImage);
        }
      }
    } catch (e) {
      console.error("Error cropping image:", e);
    } finally {
      // setLoader(false);
    }
  };
  useEffect(() => {
    // Mobile: explicitly reset/center the cropper when the modal opens.
    // Without this, the internal image transform can start "shifted" (right-aligned feel).
    if (!isOpen || !isMobile) return;
    if (!cropperRef.current) return;
    // Wait a tick for layout/measurements to settle (especially on mobile).
    requestAnimationFrame(() => {
      try {
        cropperRef.current.reset();
        cropperRef.current.refresh();
        // Some versions expose reconcileState to fit image/stencil into the boundary.
        cropperRef.current.reconcileState?.();
      } catch (e) {
        // no-op: keep cropper usable even if API differs
      }
    });
  }, [isOpen, isMobile, cropSrc]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        backgroundColor: isMobile ? "#f5f5f5" : "transparent",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: isMobile ? "55vh" : "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: isMobile ? "none" : "1px solid #e0e0e0",
          backgroundColor: "#fff",
          borderRadius: "8px",
          overflow: "hidden",
          margin: isMobile ? "8px 0" : "0 auto",
          boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
        }}
      >
        {cropSrc && (
          <Cropper
            ref={cropperRef}
            className={"cropper"}
            src={cropSrc}
            imageRestriction={isMobile ? "fitArea" : "fitArea"}
            onReady={(c) => {
              setCropper(c);
              if (!isMobile) return;
              requestAnimationFrame(() => {
                try {
                  c.reset();
                  c.refresh();
                  c.reconcileState?.();
                } catch (e) {
                  // ignore
                }
              });
            }}
            stencilProps={{
              aspectRatio: NaN,
              handlers: {
                zoomable: true,
                corners: true,
                sides: true,
                center: true,
                eastNorth: true,
                north: true,
                westNorth: true,
                west: true,
                westSouth: true,
                south: true,
                eastSouth: true,
                east: true,
              },
              grid: true,
              movable: true,
              resizable: true,
              lines: true,
            }}
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#fafafa",
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: isMobile ? "center" : "flex-end",
          gap: 2,
          mt: isMobile ? "auto" : 2,
          width: "100%",
          flexShrink: 0,
          padding: isMobile ? "16px" : "16px 0 0 0",
          backgroundColor: isMobile ? "#fff" : "transparent",
          borderTop: isMobile ? "1px solid #e0e0e0" : "none",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            if (onCancel) onCancel();
            else if (cropSrc && onImageCropped) onImageCropped(cropSrc);
          }}
          disabled={loader}
          sx={{
            fontSize: isMobile ? "15px" : "14px",
            fontWeight: 600,
            padding: isMobile ? "12px 24px" : "10px 24px",
            borderRadius: isMobile ? "8px" : "6px",
            textTransform: "none",
            flex: isMobile ? 1 : "none",
            borderColor: "rgba(0,0,0,0.3)",
            color: "rgba(0,0,0,0.7)",
            "&:hover": { borderColor: "rgba(0,0,0,0.5)", backgroundColor: "rgba(0,0,0,0.04)" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCrop}
          startIcon={loader && <CircularProgress size={20} color="inherit" />}
          disabled={loader}
          sx={{
            fontSize: isMobile ? "15px" : "14px",
            fontWeight: 600,
            padding: isMobile ? "12px 24px" : "10px 24px",
            borderRadius: isMobile ? "8px" : "6px",
            flex: isMobile ? 1 : "none",
            bgcolor: "rgb(0, 51, 153)",
            textTransform: "none",
            boxShadow: "0 2px 8px rgba(0, 51, 153, 0.3)",
            "&:hover": {
              bgcolor: "rgb(0, 41, 123)",
              boxShadow: "0 4px 12px rgba(0, 51, 153, 0.4)",
            },
            "&:disabled": {
              bgcolor: "rgba(0, 51, 153, 0.5)",
            },
          }}
        >
          {loader ? "Processing..." : "Apply"}
        </Button>
      </Box>
    </Box>
  );
};

export default ImageCropperModal;
